import {
  DecoratorNode,
  DOMConversionMap,
  DOMConversionOutput,
  DOMExportOutput,
  LexicalNode,
  NodeKey,
  SerializedLexicalNode,
  Spread
} from 'lexical';

import React from 'react';

export interface ImagePayload {
  src: string;
  altText?: string;
  width?: number;
  height?: number;
}

export type SerializedImageNode = Spread<ImagePayload & { altText: string }, SerializedLexicalNode>;

export class ImageNode extends DecoratorNode<JSX.Element> {
  public __src: string;
  public __altText: string;
  public __width?: number;
  public __height?: number;

  public static getType(): string {
    return 'image';
  }

  public static clone(node: ImageNode): ImageNode {
    return new ImageNode(node.__src, node.__altText, node.__width, node.__height, node.__key);
  }

  public static importJSON({ src, altText, width, height }: SerializedImageNode): ImageNode {
    return $createImageNode({ src, altText, width, height });
  }

  public static importDOM(): DOMConversionMap | null {
    return {
      img: () => ({
        conversion: (domNode: Node): DOMConversionOutput | null => {
          if (domNode instanceof HTMLImageElement) {
            const { src, alt: altText, width, height } = domNode;
            return { node: $createImageNode({ src, altText, width, height }) };
          }
          return null;
        },
        priority: 0
      })
    };
  }

  constructor(src: string, altText = '', width?: number, height?: number, key?: NodeKey) {
    super(key);
    this.__src = src;
    this.__altText = altText;
    this.__width = width;
    this.__height = height;
  }

  public exportJSON(): SerializedImageNode {
    return {
      type: 'image',
      version: 1,
      src: this.__src,
      altText: this.__altText,
      width: this.__width,
      height: this.__height
    };
  }

  public exportDOM(): DOMExportOutput {
    const img = document.createElement('img');
    img.src = this.__src;
    img.alt = this.__altText;
    if (this.__width) img.width = this.__width;
    if (this.__height) img.height = this.__height;
    return { element: img };
  }

  public createDOM(): HTMLElement {
    return document.createElement('span');
  }

  public updateDOM(): false {
    return false;
  }

  public decorate(): JSX.Element {
    return <ImageNodeComponent src={this.__src} altText={this.__altText} width={this.__width} height={this.__height} />;
  }
}

const ImageNodeComponent: React.FC<{ src: string; altText: string; width?: number; height?: number }> = ({
  src,
  altText,
  width,
  height
}) => (
  <img
    src={src}
    alt={altText}
    width={width}
    height={height}
    style={{ maxWidth: '100%', height: 'auto', display: 'block', margin: '8px 0' }}
  />
);

export function $createImageNode({ src, altText, width, height }: ImagePayload): ImageNode {
  return new ImageNode(src, altText, width, height);
}

export function $isImageNode(node: LexicalNode | null | undefined): node is ImageNode {
  return node instanceof ImageNode;
}
